import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import CONFIG from '../../config.js';
import CalendarioResumenEventos from '../mensajes/CalendarioResumenEventos.js';
import './Principal.css';

const URL_CALENDARIO = `${CONFIG.API_URL}/operarCalendario.php`;
const URL_CURSO_PRECEPTOR = `${CONFIG.API_URL}/operarCursoPreceptor.php`;

function PrincipalPreceptor({ configuracion, rol }) {
    const navigate = useNavigate();
    const userId = localStorage.getItem('loggedUserId');
    const [eventos, setEventos] = useState([]);
    const [misCursos, setMisCursos] = useState([]);
    const [cargandoCursos, setCargandoCursos] = useState(true);

    useEffect(() => {
        obtenerEventos();
        obtenerMisCursos();
    }, []);

    const obtenerMisCursos = async () => {
        setCargandoCursos(true);
        try {
            const res = await axios.get(`${URL_CURSO_PRECEPTOR}?accion=mis_cursos&id_usuario=${userId}`);
            if (res.data && res.data.resultado && Array.isArray(res.data.cursos)) {
                setMisCursos(res.data.cursos);
            } else {
                setMisCursos([]);
            }
        } catch (err) {
            console.error('Error al obtener cursos preceptor:', err);
            setMisCursos([]);
        } finally {
            setCargandoCursos(false);
        }
    };

    const obtenerEventos = async () => {
        try {
            const res = await axios.get(`${URL_CALENDARIO}?id_usuario=${userId}`);
            if (Array.isArray(res.data)) {
                const tiposPreceptor = ['todos', 'todosA', 'todosD', 'todosE', 'todosT', 'todosM', 'todosDC', 'todosEC', 'todosTC', 'todosDETC'];
                const eventosFiltrados = res.data.filter(evento => {
                    const esPropio = evento.creada_por == userId;
                    return esPropio || tiposPreceptor.includes(evento.tipo_recordatorio);
                });

                // Deduplicar eventos que provengan de múltiples curso_grupo
                const eventosUnicos = [];
                const clavesVistas = new Set();
                eventosFiltrados.forEach(evt => {
                    const soloFecha = (evt.fecha || '').split(' ')[0];
                    const clave = `${soloFecha}_${evt.hora_desde}_${evt.hora_hasta}_${evt.evento}_${evt.creada_por}_${evt.tipo_recordatorio}`;
                    if (!clavesVistas.has(clave)) {
                        clavesVistas.add(clave);
                        eventosUnicos.push(evt);
                    }
                });
                setEventos(eventosUnicos);
            } else {
                setEventos([]);
            }
        } catch (err) {
            console.error('Error al obtener eventos preceptor:', err);
        }
    };

    const colorPrimary = configuracion.color_principal || '#014751';
    const colorSecondary = configuracion.color_secundario || '#0ac694';

    const accesos = [
        {
            titulo: 'Cargar Asistencia',
            descripcion: 'Registrar y administrar asistencia diaria de los estudiantes por curso.',
            icono: 'fa-clipboard-user',
            link: '/CargarAsistencia',
            colorGrad1: colorPrimary,
            colorGrad2: colorSecondary,
            badgeText: 'Asistencia'
        },
        {
            titulo: 'Inasistencias por Estudiante',
            descripcion: 'Buscar un estudiante por nombre, DNI o curso para ver su gráfica y detalle de asistencias.',
            icono: 'fa-user-check',
            link: '/InasistenciasEstudiante',
            colorGrad1: colorPrimary,
            colorGrad2: colorSecondary,
            badgeText: 'Detalle e Histórico'
        },
        {
            titulo: 'Reportes',
            descripcion: 'Consultar listas de estudiantes por curso e ingresar a las fichas de estudiantes.',
            icono: 'fa-chart-pie',
            link: '/Reportes',
            colorGrad1: colorSecondary,
            colorGrad2: colorPrimary,
            badgeText: 'Listados y Fichas'
        },
        {
            titulo: 'Calificaciones Generales',
            descripcion: 'Visualización general de calificaciones cargadas por curso e instancia.',
            icono: 'fa-star',
            link: '/CalificacionesGeneral',
            colorGrad1: colorSecondary,
            colorGrad2: colorPrimary,
            badgeText: 'Vista de Calificaciones'
        }
    ];

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="fw-bold text-dark m-0">
                    <i className="fa-solid fa-user-shield text-primary me-2"></i>
                    Panel de Preceptoría
                </h4>
            </div>

            {/* Indicador de Cursos Asignados para Preceptor */}
            {misCursos.length > 0 ? (
                <div className="card border-0 shadow-sm rounded-4 mb-4 bg-white overflow-hidden">
                    <div className="card-body p-3 p-md-4">
                        <div className="d-flex align-items-start align-items-md-center justify-content-between flex-column flex-md-row gap-3">
                            <div className="d-flex align-items-center gap-3">
                                <div 
                                    className="rounded-4 d-flex align-items-center justify-content-center text-white shadow-sm flex-shrink-0"
                                    style={{
                                        width: '48px',
                                        height: '48px',
                                        background: `linear-gradient(135deg, ${colorPrimary} 0%, ${colorSecondary} 100%)`,
                                        fontSize: '1.4rem'
                                    }}
                                >
                                    <i className="fa-solid fa-graduation-cap"></i>
                                </div>
                                <div>
                                    <div className="d-flex align-items-center gap-2">
                                        <h5 className="fw-bold mb-0 text-dark">Cursos Asignados</h5>
                                        <span className="badge rounded-pill bg-success-subtle text-success border border-success-subtle px-2 py-1 small">
                                            {misCursos.length} {misCursos.length === 1 ? 'curso' : 'cursos'}
                                        </span>
                                    </div>
                                    <p className="text-muted small mb-0 mt-1">
                                        Usted se encuentra formalmente asignado a los siguientes cursos:
                                    </p>
                                </div>
                            </div>

                            <div className="d-flex flex-wrap gap-2">
                                {misCursos.map((c, idx) => (
                                    <div 
                                        key={idx} 
                                        className="card border border-1 shadow-none rounded-3 px-3 py-2 bg-light d-flex flex-row align-items-center gap-2"
                                    >
                                        <span className="badge bg-primary text-white rounded-pill px-2 py-1">
                                            {c.orden}° "{c.seccion}"
                                        </span>
                                        <div className="d-flex flex-column">
                                            <span className="small fw-bold text-dark">{c.nombre_formacion || 'Formación'}</span>
                                            <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                Cohorte {c.cohorte_año} {c.turno_contraturno ? `• ${c.turno_contraturno}` : ''}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                !cargandoCursos && (
                    <div className="alert alert-light border shadow-sm rounded-4 mb-4 d-flex align-items-center gap-3 py-3 px-3">
                        <i className="fa-solid fa-circle-info text-info fs-4"></i>
                        <div className="small text-muted">
                            <strong className="text-dark">Acceso General:</strong> Al no tener cursos específicos asignados en el registro institucional, usted dispone de acceso a la totalidad de los cursos del establecimiento.
                        </div>
                    </div>
                )
            )}

            <div className="row g-4">
                {/* Columna Izquierda: Accesos Directos (4 Módulos) */}
                <div className="col-12 col-lg-8">
                    <div className="card border-0 shadow-sm rounded-4 overflow-hidden mb-4 bg-light">
                        <div className="card-header bg-white border-bottom-0 pt-4 pb-1 px-4">
                            <h5 className="fw-bold text-dark m-0">
                                <i className="fa-solid fa-grid-2 me-2 text-primary"></i>
                                Módulos Habilitados
                            </h5>
                            <p className="text-muted small mt-1 mb-0">Acceso rápido a las pantallas y funciones del preceptor</p>
                        </div>
                        <div className="card-body p-4">
                            <div className="row g-3">
                                {accesos.map((acc, index) => (
                                    <div key={index} className="col-12 col-md-6">
                                        <div 
                                            className="card h-100 border-0 shadow-sm hover-shadow-lg transition-all rounded-3 p-3 text-white"
                                            style={{
                                                background: `linear-gradient(135deg, ${acc.colorGrad1} 0%, ${acc.colorGrad2} 100%)`,
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => navigate(acc.link)}
                                        >
                                            <div className="card-body p-2 d-flex flex-column justify-content-between">
                                                <div>
                                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                                        <span className="fs-2 text-white opacity-90">
                                                            <i className={`fa-solid ${acc.icono}`}></i>
                                                        </span>
                                                        <span className="badge bg-white text-dark opacity-90 rounded-pill px-3 py-1 small fw-bold">
                                                            {acc.badgeText}
                                                        </span>
                                                    </div>
                                                    <h5 className="fw-bold text-white mb-2">{acc.titulo}</h5>
                                                    <p className="text-white-50 small mb-3">{acc.descripcion}</p>
                                                </div>
                                                <div className="pt-2 border-top border-white border-opacity-25 d-flex justify-content-between align-items-center">
                                                    <span className="small fw-semibold text-white">Ingresar módulo</span>
                                                    <i className="fa-solid fa-arrow-right text-white"></i>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Columna Derecha: Resumen de Agenda */}
                <div className="col-12 col-lg-4">
                    <div className="card border-0 shadow-sm rounded-4 h-100 overflow-hidden">
                        <div className="card-header bg-white border-bottom-0 pt-4 pb-0 px-4">
                            <h6 className="fw-bold text-dark mb-0">
                                <i className="fa-regular fa-calendar-days text-primary me-2"></i>
                                Resumen de Agenda
                            </h6>
                        </div>
                        <div className="card-body p-4 pt-3">
                            <CalendarioResumenEventos eventos={eventos} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PrincipalPreceptor;
